import React, { useState, useRef, useEffect } from 'react';
import SignatureModal from '../SignatureModal';

interface PracticePoliciesProps {
  onAccept: (signature: string) => void;
}

const PracticePolicies: React.FC<PracticePoliciesProps> = ({ onAccept }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signature, setSignature] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
      setHasScrolledToBottom(isAtBottom);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAgreed(e.target.checked);
  };

  const handleSignatureComplete = (signatureData: string, signatureType: 'drawn' | 'typed', typedName?: string) => {
    setSignature(signatureData);
    setShowSignatureModal(false);
  };

  const handleSubmit = () => {
    if (signature) {
      onAccept(signature);
    }
  };

  return (
    <div className="app">
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        background: '#28527A',
        borderRadius: '16px',
        padding: '2rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ color: '#f9fafb', fontSize: '2rem', margin: '0 0 0.5rem 0' }}>
            The Real Therapists of Atlanta
          </h1>
          <p style={{ color: '#6CA6C1', margin: '0 0 1rem 0', fontSize: '1rem' }}>
            626 Main Street | Palmetto, GA. 30268 | (470).250.9113
          </p>
          <h1 style={{ color: '#f9fafb', fontSize: '1.75rem', margin: '0 0 0.5rem 0' }}>
            Practice Policies
          </h1>
          <p style={{ color: '#6CA6C1', margin: 0 }}>
            Please read the practice policies below and scroll to the bottom to continue
          </p>
        </div>

        {/* Policy Content */}
        <div style={{
          background: '#113F67',
          borderRadius: '12px',
          border: '1px solid #6CA6C1',
          marginBottom: '2rem'
        }}>
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            style={{
              height: '400px',
              overflowY: 'auto',
              padding: '2rem',
              color: '#d1d5db',
              fontSize: '0.9rem',
              lineHeight: '1.6'
            }}
          >
            <h2 style={{ color: '#f9fafb', marginBottom: '1.5rem', textAlign: 'center' }}>
              PRACTICE POLICIES
            </h2>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              APPOINTMENTS AND CANCELLATIONS
            </h3>
            <p style={{ marginBottom: '1rem' }}>
              Please remember to cancel or reschedule 24 hours in advance. You will be responsible for the entire fee if cancellation is less than 24 hours.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              The standard meeting time for psychotherapy is 50 minutes. It is up to you, however, to determine the length of time of your sessions. Requests to change the 50-minute session needs to be discussed with the therapist in order for time to be scheduled in advance.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              A $10.00 service charge will be charged for any checks returned for any reason for special handling.
            </p>
            <p style={{ marginBottom: '1.5rem' }}>
              Cancellations and re-scheduled session will be subject to a full charge if NOT RECEIVED AT LEAST 24 HOURS IN ADVANCE. This is necessary because a time commitment is made to you and is held exclusively for you. If you are late for a session, you may lose some of that session time.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              TELEPHONE ACCESSIBILITY
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              If you need to contact me between sessions, please leave a message on my voice mail. I am often not immediately available; however, I will attempt to return your call within 24 hours. Please note that Face-to-face sessions are highly preferable to phone sessions. However, in the event that you are out of town, sick or need additional support, phone sessions are available. If a true emergency situation arises, please call 911 or any local emergency room.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              SOCIAL MEDIA AND TELECOMMUNICATION
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              Due to the importance of your confidentiality and the importance of minimizing dual relationships, I do not accept friend or contact requests from current or former clients on any social networking site (Facebook, LinkedIn, etc). I believe that adding clients as friends or contacts on these sites can compromise your confidentiality and our respective privacy. It may also blur the boundaries of our therapeutic relationship. If you have questions about this, please bring them up when we meet and we can talk more about it.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              ELECTRONIC COMMUNICATION
            </h3>
            <p style={{ marginBottom: '1rem' }}>
              I cannot ensure the confidentiality of any form of communication through electronic media, including text messages. If you prefer to communicate via email or text messaging for issues regarding scheduling or cancellations, I will do so. While I may try to return messages in a timely manner, I cannot guarantee immediate response and request that you do not use these methods of communication to discuss therapeutic content and/or request assistance for emergencies.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              Services by electronic means, including but not limited to telephone communication, the Internet, facsimile machines, and e-mail is considered telemedicine by the State of California. Under the California Telemedicine Act of 1996, telemedicine is broadly defined as the use of information technology to deliver medical services and information from one location to another. If you and your therapist chose to use information technology for some or all of your treatment, you need to understand that:
            </p>
            <ol style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>
                You retain the option to withhold or withdraw consent at any time without affecting the right to future care or treatment or risking the loss or withdrawal of any program benefits to which you would otherwise be entitled.
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                All existing confidentiality protections are equally applicable.
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Your access to all medical information transmitted during a telemedicine consultation is guaranteed, and copies of this information are available for a reasonable fee.
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Dissemination of any of your identifiable images or information from the telemedicine interaction to researchers or other entities shall not occur without your consent.
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                There are potential risks, consequences, and benefits of telemedicine. Potential benefits include, but are not limited to improved communication capabilities, providing convenient access to up-to-date information, consultations, support, reduced costs, improved quality, change in the conditions of practice, improved access to therapy, better continuity of care, and reduction of lost work time and travel costs. Effective therapy is often facilitated when the therapist gathers within a session or a series of sessions, a multitude of observations, information, and experiences about the client. Therapists may make clinical assessments, diagnosis, and interventions based not only on direct verbal or auditory communications, written reports, and third person consultations, but also from direct visual and olfactory observations, information, and experiences. When using information technology in therapy services, potential risks include, but are not limited to the therapist's inability to make visual and olfactory observations of clinically or therapeutically potentially relevant issues such as: your physical condition including deformities, apparent height and weight, body type, attractiveness relative to social and cultural norms or standards, gait and motor coordination, posture, work speed, any noteworthy mannerism or gestures, physical or medical conditions including bruises or injuries, basic grooming and hygiene including appropriateness of dress, eye contact (including any changes in the previously listed issues), sex, chronological and apparent age, ethnicity, facial and body language, and congruence of language and facial or bodily expression. Potential consequences thus include the therapist not being aware of what he or she would consider important information, that you may not recognize as significant to present verbally the therapist.
              </li>
            </ol>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              MINORS
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              If you are a minor, your parents may be legally entitled to some information about your therapy. I will discuss with you and your parents what information is appropriate for them to receive and which issues are more appropriately kept confidential.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              TERMINATION
            </h3>
            <p style={{ marginBottom: '1rem' }}>
              Ending relationships can be difficult. Therefore, it is important to have a termination process in order to achieve some closure. The appropriate length of the termination depends on the length and intensity of the treatment. I may terminate treatment after appropriate discussion with you and a termination process if I determine that the psychotherapy is not being effectively used or if you are in default on payment. I will not terminate the therapeutic relationship without first discussing and exploring the reasons and purpose of terminating. If therapy is terminated for any reason or you request another therapist, I will provide you with a list of qualified psychotherapists to treat you. You may also choose someone on your own or from another referral source.
            </p>
            <p style={{ marginBottom: '1.5rem' }}>
              Should you fail to schedule an appointment for three consecutive weeks, unless other arrangements have been made in advance, for legal and ethical reasons, I must consider the professional relationship discontinued.
            </p>

            <p style={{ marginBottom: '1.5rem', fontWeight: 'bold', color: '#f9fafb', textTransform: 'uppercase' }}>
              BY SIGNING BELOW I AM AGREEING THAT I HAVE READ, UNDERSTOOD AND AGREE TO THE ITEMS CONTAINED IN THIS DOCUMENT.
            </p>

            <p style={{ marginBottom: '0', fontSize: '0.8rem', color: '#d1d5db' }}>
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Agreement Section */}
        <div style={{
          background: '#113F67',
          borderRadius: '12px',
          padding: '1.5rem',
          border: '1px solid #6CA6C1',
          marginBottom: '1.5rem'
        }}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              color: hasScrolledToBottom ? '#f9fafb' : '#6b7280',
              cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed'
            }}>
              <input
                type="checkbox"
                checked={isAgreed}
                onChange={handleCheckboxChange}
                disabled={!hasScrolledToBottom}
                style={{
                  marginRight: '0.75rem',
                  width: '18px',
                  height: '18px',
                  cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed'
                }}
              />
              I have read, understood, and agree to the Practice Policies
            </label>
          </div>

          {/* Signature Section */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: isAgreed ? '#f9fafb' : '#6b7280',
              marginBottom: '0.5rem',
              fontWeight: '500'
            }}>
              Digital Signature *
            </label>
            <button
              type="button"
              onClick={() => setShowSignatureModal(true)}
              disabled={!isAgreed}
              style={{
                width: '100%',
                padding: '3rem 1rem',
                borderRadius: '8px',
                border: '2px dashed #6CA6C1',
                background: isAgreed ? '#374151' : '#1f2937',
                color: isAgreed ? '#f9fafb' : '#6b7280',
                fontSize: '1rem',
                cursor: isAgreed ? 'pointer' : 'not-allowed',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
            >
              {signature ? (
                <div>
                  <div style={{ marginBottom: '0.5rem', color: '#10b981' }}>✓ Signature Captured</div>
                  <div style={{ fontSize: '0.875rem' }}>Click to update signature</div>
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: '0.5rem' }}>📝</div>
                  <div>Click to add your digital signature</div>
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleSubmit}
            disabled={!signature}
            style={{
              background: signature
                ? 'linear-gradient(135deg, #D4A017 0%, #B8860B 100%)'
                : '#6b7280',
              color: 'white',
              padding: '0.75rem 3rem',
              fontSize: '1rem',
              fontWeight: '600',
              border: 'none',
              borderRadius: '8px',
              cursor: signature ? 'pointer' : 'not-allowed',
              transition: 'all 0.3s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              boxShadow: signature
                ? '0 4px 14px 0 rgba(212, 160, 23, 0.3)'
                : 'none'
            }}
          >
            Submit Agreement
          </button>
        </div>

        {/* Progress Indicator */}
        <div style={{
          textAlign: 'center',
          marginTop: '1.5rem',
          padding: '1rem',
          background: '#065f46',
          borderRadius: '8px',
          border: '1px solid #10b981'
        }}>
          <div style={{ color: '#d1d5db', fontSize: '0.875rem' }}>
            Progress:
            <span style={{ color: hasScrolledToBottom ? '#10b981' : '#6b7280', marginLeft: '0.5rem' }}>
              ✓ Read Policies
            </span>
            <span style={{ color: isAgreed ? '#10b981' : '#6b7280', marginLeft: '1rem' }}>
              ✓ Accept Terms
            </span>
            <span style={{ color: signature ? '#10b981' : '#6b7280', marginLeft: '1rem' }}>
              ✓ Digital Signature
            </span>
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      {showSignatureModal && (
        <SignatureModal
          isOpen={showSignatureModal}
          onClose={() => setShowSignatureModal(false)}
          onSignatureComplete={handleSignatureComplete}
          title="Practice Policies Signature"
          clientName="Client"
          previousSignature={signature ? {
            signatureData: signature,
            signatureType: 'drawn' as const
          } : undefined}
        />
      )}
    </div>
  );
};

export default PracticePolicies;